import { gsap } from '@theme/gsap'
import ScrollTrigger  from '@theme/gsap/ScrollTrigger'
import  MotionPathPlugin from '@theme/gsap/MotionPathPlugin'
import {debounce, restoreScrollPrevPosition} from '@theme/utilities'


//Register plugins
gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

if (!customElements.get('motion-rail')) {
  customElements.define('motion-rail',

    class MotionRail extends HTMLElement {
      static get observedAttributes() {
        return ["end", "ease", "scrub", "pin", "start"];
      }

      constructor() {
        super();
        this.tl = null;
        this.id = this.getAttribute('id');
        this._refresh = this._refresh.bind(this);
        this._onResize = debounce(this._refresh, 1000);
      }

      connectedCallback() {

        // Build after layout is ready
        requestAnimationFrame(() => {
          if (!this.isConnected) return;
          this._init();
          ScrollTrigger.refresh();

          // probably we dont need it
          // restoreScrollPrevPosition()
        });

        // Keep it resilient when images/fonts load or viewport changes
        window.addEventListener("resize", this._onResize);
        let lastHeight = document.body.scrollHeight;

        const observer = new MutationObserver(() => {
          const currentHeight = document.body.scrollHeight;
          if (currentHeight !== lastHeight) {
            lastHeight = currentHeight;

            setTimeout(() => {
              // Skip if animation already completed — avoid jump when
              // DOM changes (e.g. Swiper cloning) happen after user scrolled past
              const st = ScrollTrigger.getById(this.id);
              if (st && st.progress >= 1) return;
              // console.log(currentHeight,'Content changed, refreshing ScrollTrigger');
              this._refresh();
            }, 200);
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          characterData: true,
        });
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this._onResize);
        this._destroy();
      }

      attributeChangedCallback() {
        // Rebuild when config attrs change
        this._refresh();
      }

      _init() {
        // Cleanup previous
        this._destroy();
        // If removed before init runs
        if (!this.isConnected) return;

        const stage = this.querySelector("[motion-rail-container]");
        if (!stage) return;

        const cards = gsap.utils.toArray(stage.querySelectorAll("[motion-rail-card]"));
        const path =
          stage.querySelector("[motion-rail-path]");

        if (!cards.length || !path) return;

        const end = Number(this.getAttribute("end") ?? 4000);
        const ease = this.getAttribute("ease") ?? "power2.outIn";
        const start = this.getAttribute("start") ?? "top top";

        const scrubAttr = this.getAttribute("scrub");
        const pinAttr = this.getAttribute("pin");

        const scrub = scrubAttr === null ? true : scrubAttr === "true" ? true : Number(scrubAttr) || true;
        const pin = pinAttr === null ? true : pinAttr === "true";

        
        // Create timeline with a scoped ScrollTrigger
        this.tl = gsap.timeline({
          scrollTrigger: {
            id: this.id,
            trigger: stage,
            start,
            end: `+=${end}`,
            scrub: 1.5,
            pin,
            invalidateOnRefresh: true,
            anticipatePin: true
            // markers: true
          }
        });

        // Set initial cards
        gsap.set(cards, {
          motionPath: {
            path,
            align: path,
            alignOrigin: [0.5, 0.5],
            autoRotate: true,
            start: 1,
            end: 1
          }
        });

        const releaseAt = Number(this.getAttribute("release-at") ?? 0.5); // when next starts (0..1)
        const moveDuration = Number(this.getAttribute("move-duration") ?? 1); // timeline units
        const overlap = moveDuration * releaseAt; // next start when previous reaches overlap

        // Total timeline calculation
        const total = moveDuration + overlap * (cards.length - 1);
        let offsetTotal = 0;

        // each card tween starts when previous reaches overlap
        cards.forEach((card, i) => {
          const startValue = 0.5 + (i * releaseAt);
          const startPosition = startValue > 1 ? 1 : startValue;
          //0 0 0
          let offset;
          if(startValue > 1) {
            offsetTotal += overlap;
            offset = offsetTotal;
          } else {
            offset = 0
          }

          this.tl.to(
            card,
            {
              duration: startPosition,
              ease,
              motionPath: {
                path,
                align: path,
                alignOrigin: [0.5, 0.5],
                autoRotate: true,
                start: startPosition,
                end: 0
              }
            },
            offset
          );
        });

        // final element, that should appear when last card reaches 50% of the path
        const finalEl = this.querySelector("[motion-rail-final]");
        if (finalEl) gsap.set(finalEl, { autoAlpha: 0, scale: 0, xPercent: -50, yPercent: -50 });

        if (finalEl) {
          this.tl.set(finalEl, { autoAlpha: 1 }, offsetTotal + 0.5);

          this.tl.fromTo(
            finalEl,
            { scale: 0.4, xPercent: -50, yPercent: -50 },
            {
              scale: 1,
              duration: 0.8,
              ease: "power2.out"
            },
            offsetTotal + 0.5
          );
        }

        // timeline duration
        this.tl.duration(total);
      }

      _destroy() {
        if (!this.tl) return;
        ScrollTrigger.getById(this.id)?.kill(true);
        document.querySelectorAll(".js-animation-item").forEach((el) => {
          gsap.set(el, { clearProps: "all" });
        });
        // Kill ScrollTrigger tied to this timeline
        this.tl.kill();
        this.tl = null;
      }

      _refresh() {
        // console.log('Refreshing MotionRail');
        // Avoid rebuild loops if called too early
        if (!this.isConnected) return;
        const y = window.scrollY;

        this._init();
        ScrollTrigger.refresh();

        window.scrollTo(0, y);
        // ScrollTrigger.refresh();
      }
    }
  )
}

